import React, { useState, useRef, useEffect } from "react";
import { useUserProfiles } from "../../contexts/UserProfileContext";
import { useUser } from "../../contexts/UserContext";
import {
  FaDice,
  FaEdit,
  FaTrash,
  FaPlus,
  FaCheck,
  FaTimes,
  FaUndo,
  FaUser,
  FaUserAlt,
  FaCrown,
} from "react-icons/fa";
import { IoMdMan, IoMdWoman } from "react-icons/io";
import "./UserProfileManager.css";

const UserProfileManager = ({ onClose }) => {
  const {
    profiles,
    activeProfile,
    createProfile,
    updateProfile,
    deleteProfile,
    switchProfile,
    updateProfileSettings,
  } = useUserProfiles();

  const { subscriptionStatus } = useUser();

  const [isCreating, setIsCreating] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [newProfileAge, setNewProfileAge] = useState("");
  const [newProfileGender, setNewProfileGender] = useState("");
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [initialStates, setInitialStates] = useState({});
  const colorInputRefs = useRef({});

  // Store initial states when profiles change
  useEffect(() => {
    const states = {};
    profiles.forEach((profile) => {
      if (!initialStates[profile.id]) {
        states[profile.id] = {
          avatar: profile.avatar,
          color: profile.settings?.favoriteColor || "#6a11cb",
          age: profile.age,
          gender: profile.gender,
        };
      } else {
        states[profile.id] = initialStates[profile.id];
      }
    });
    setInitialStates(states);
  }, [profiles.length]); // Only run when profiles are added/removed

  const handleUndoChanges = (profileId) => {
    const initial = initialStates[profileId];
    if (initial) {
      updateProfile(profileId, {
        ...profiles.find((p) => p.id === profileId),
        avatar: initial.avatar,
        age: initial.age,
        gender: initial.gender,
        settings: {
          favoriteColor: initial.color,
        },
      });
    }
  };

  const hasChanges = (profile) => {
    const initial = initialStates[profile.id];
    if (!initial) return false;

    return (
      initial.avatar !== profile.avatar ||
      initial.color !== (profile.settings?.favoriteColor || "#6a11cb") ||
      initial.age !== profile.age ||
      initial.gender !== profile.gender
    );
  };

  const getRandomColor = () => {
    // Generate vibrant colors by using HSL
    const hue = Math.floor(Math.random() * 360); // Random hue (0-360)
    const saturation = Math.floor(Math.random() * 20 + 80); // High saturation (80-100)
    const lightness = Math.floor(Math.random() * 20 + 40); // Medium lightness (40-60)

    // Convert HSL to Hex
    const h = hue / 360;
    const s = saturation / 100;
    const l = lightness / 100;

    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
    const g = Math.round(hue2rgb(p, q, h) * 255);
    const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

    return (
      "#" +
      [r, g, b]
        .map((x) => {
          const hex = x.toString(16);
          return hex.length === 1 ? "0" + hex : hex;
        })
        .join("")
    );
  };

  // Check if user has premium subscription
  const isPremiumUser = subscriptionStatus?.status === "active";
  const canAddMoreProfiles = isPremiumUser || profiles.length === 0;

  const handleCreateProfile = (e) => {
    e.preventDefault();

    // Check if free user is trying to add more than 1 profile
    if (!isPremiumUser && profiles.length >= 1) {
      alert(
        "Free users can only have 1 profile. Upgrade to Premium to create unlimited profiles!"
      );
      return;
    }

    if (newProfileName.trim()) {
      const profileData = {
        name: newProfileName.trim(),
        avatar: `https://api.dicebear.com/6.x/fun-emoji/svg?seed=${Date.now()}`,
        settings: {
          favoriteColor: getRandomColor(),
        },
      };

      // Add age if provided
      if (newProfileAge && !isNaN(newProfileAge) && newProfileAge > 0) {
        profileData.age = parseInt(newProfileAge);
      }

      // Add gender if selected
      if (newProfileGender) {
        profileData.gender = newProfileGender;
      }

      createProfile(profileData);
      setNewProfileName("");
      setNewProfileAge("");
      setNewProfileGender("");
      setIsCreating(false);
    }
  };

  const startEditingName = (profile) => {
    setEditingNameId(profile.id);
    setEditingNameValue(profile.name);
  };

  const saveProfileName = () => {
    if (editingNameValue.trim() && editingNameId) {
      updateProfile(editingNameId, {
        name: editingNameValue.trim(),
      });
      setEditingNameId(null);
      setEditingNameValue("");
    }
  };

  const cancelEditingName = () => {
    setEditingNameId(null);
    setEditingNameValue("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      saveProfileName();
    } else if (e.key === "Escape") {
      cancelEditingName();
    }
  };

  const handleDeleteProfile = (profileId) => {
    if (profiles.length > 1) {
      if (window.confirm("Are you sure you want to delete this profile?")) {
        deleteProfile(profileId);
      }
    } else {
      alert("You must keep at least one profile.");
    }
  };

  const handleRandomizeAvatar = (profile) => {
    updateProfile(profile.id, {
      ...profile,
      avatar: `https://api.dicebear.com/6.x/fun-emoji/svg?seed=${Date.now()}`,
    });
  };

  const handleBackgroundClick = (profileId) => {
    colorInputRefs.current[profileId]?.click();
  };

  const handleAgeChange = (profileId, age) => {
    const ageValue = age === "" ? null : parseInt(age);
    if (age === "" || (!isNaN(ageValue) && ageValue > 0 && ageValue <= 120)) {
      updateProfile(profileId, { age: ageValue });
    }
  };

  const handleGenderChange = (profileId, gender) => {
    updateProfile(profileId, { gender: gender || null });
  };

  const handleUpgradeToPremium = () => {
    // Close the profile manager and trigger subscription management
    onClose();

    // Dispatch a custom event to trigger subscription modal
    window.dispatchEvent(new CustomEvent("openSubscriptionModal"));
  };

  return (
    <div className="user-profile-manager">
      <div className="profiles-header">
        <h2>User Profiles</h2>
        {canAddMoreProfiles ? (
          <button
            className="create-profile-btn"
            onClick={() => setIsCreating(true)}
          >
            <FaPlus /> Add Profile
          </button>
        ) : (
          <div className="premium-upgrade-section">
            <div className="profile-limit-info">
              <small>Free users: 1 profile limit</small>
            </div>
            <button
              className="upgrade-premium-btn"
              onClick={handleUpgradeToPremium}
            >
              <FaCrown /> Upgrade to Premium
            </button>
          </div>
        )}
      </div>

      {!isPremiumUser && profiles.length >= 1 && (
        <div className="profile-limit-notice">
          <div className="limit-notice-content">
            <FaCrown className="crown-icon" />
            <div className="notice-text">
              <strong>Profile Limit Reached</strong>
              <p>
                Free users can have 1 profile. Upgrade to Premium for unlimited
                profiles and access to all premium games!
              </p>
            </div>
            <button
              className="upgrade-notice-btn"
              onClick={handleUpgradeToPremium}
            >
              <FaCrown /> Upgrade Now
            </button>
          </div>
        </div>
      )}

      {isCreating && (
        <div className="profile-form">
          <form onSubmit={handleCreateProfile}>
            <div className="form-row">
              <input
                type="text"
                value={newProfileName}
                onChange={(e) => setNewProfileName(e.target.value)}
                placeholder="Enter profile name"
                required
                autoFocus
              />
            </div>

            <div className="form-row">
              <input
                type="number"
                value={newProfileAge}
                onChange={(e) => setNewProfileAge(e.target.value)}
                placeholder="Age (optional)"
                min="1"
                max="120"
              />

              <div className="gender-selection">
                <button
                  type="button"
                  className={`gender-btn girl ${
                    newProfileGender === "girl" ? "selected" : ""
                  }`}
                  onClick={() =>
                    setNewProfileGender(
                      newProfileGender === "girl" ? "" : "girl"
                    )
                  }
                >
                  <IoMdWoman /> Girl
                </button>
                <button
                  type="button"
                  className={`gender-btn boy ${
                    newProfileGender === "boy" ? "selected" : ""
                  }`}
                  onClick={() =>
                    setNewProfileGender(newProfileGender === "boy" ? "" : "boy")
                  }
                >
                  <IoMdMan /> Boy
                </button>
              </div>
            </div>

            <div className="form-buttons">
              <button type="submit" className="primary-btn">
                Create
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setIsCreating(false);
                  setNewProfileName("");
                  setNewProfileAge("");
                  setNewProfileGender("");
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="profiles-list">
        {[...profiles]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((profile) => (
            <div key={profile.id} className="profile-card">
              <div className="profile-name-section">
                {editingNameId === profile.id ? (
                  <div className="name-edit-container">
                    <input
                      type="text"
                      value={editingNameValue}
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      onKeyDown={handleKeyPress}
                      autoFocus
                      className="name-edit-input"
                    />
                    <button
                      className="name-edit-btn save"
                      onClick={saveProfileName}
                    >
                      <FaCheck />
                    </button>
                    <button
                      className="name-edit-btn cancel"
                      onClick={cancelEditingName}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="name-display">
                    <div className="name-with-badge">
                      <span
                        className={`profile-name ${
                          profile.id === activeProfile?.id ? "active" : ""
                        }`}
                      >
                        {profile.name}
                      </span>
                      {profile.id === activeProfile?.id && (
                        <span className="active-badge">Active</span>
                      )}
                    </div>
                    <button
                      className="name-edit-btn"
                      onClick={() => startEditingName(profile)}
                    >
                      <FaEdit />
                    </button>
                  </div>
                )}
              </div>
              <div className="profile-content">
                <div className="profile-left">
                  <input
                    ref={(el) => (colorInputRefs.current[profile.id] = el)}
                    type="color"
                    className="color-picker"
                    value={profile.settings?.favoriteColor || "#6a11cb"}
                    onChange={(e) =>
                      updateProfileSettings(profile.id, {
                        favoriteColor: e.target.value,
                      })
                    }
                    title="Choose favorite color"
                  />
                  <div
                    className="avatar-section"
                    style={{
                      backgroundColor:
                        profile.settings?.favoriteColor || "#6a11cb",
                    }}
                    onClick={() => handleBackgroundClick(profile.id)}
                  >
                    <img
                      src={profile.avatar}
                      alt={`${profile.name}'s avatar`}
                      width={120}
                      height={120}
                      className="profile-avatar"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRandomizeAvatar(profile);
                      }}
                      title="Click to randomize avatar"
                    />
                  </div>
                </div>

                <div className="profile-right">
                  <div className="profile-field">
                    <label>Age</label>
                    <input
                      type="number"
                      value={profile.age || ""}
                      onChange={(e) =>
                        handleAgeChange(profile.id, e.target.value)
                      }
                      placeholder="Age"
                      min="1"
                      max="120"
                      className="age-input"
                    />
                  </div>

                  <div className="profile-field">
                    <label>Gender</label>
                    <div className="gender-selection">
                      <button
                        type="button"
                        className={`gender-btn girl ${
                          profile.gender === "girl" ? "selected" : ""
                        }`}
                        onClick={() =>
                          handleGenderChange(
                            profile.id,
                            profile.gender === "girl" ? null : "girl"
                          )
                        }
                      >
                        <IoMdWoman />
                      </button>
                      <button
                        type="button"
                        className={`gender-btn boy ${
                          profile.gender === "boy" ? "selected" : ""
                        }`}
                        onClick={() =>
                          handleGenderChange(
                            profile.id,
                            profile.gender === "boy" ? null : "boy"
                          )
                        }
                      >
                        <IoMdMan />
                      </button>
                    </div>
                  </div>

                  <div className="profile-actions">
                    <button
                      className="action-btn switch-btn"
                      onClick={() => switchProfile(profile.id)}
                      disabled={profile.id === activeProfile?.id}
                    >
                      {profile.id === activeProfile?.id ? "Active" : "Switch"}
                    </button>

                    <button
                      className={`action-btn undo-btn ${
                        !initialStates[profile.id] ||
                        (profile.avatar === initialStates[profile.id]?.avatar &&
                          profile.age === initialStates[profile.id]?.age &&
                          profile.gender ===
                            initialStates[profile.id]?.gender &&
                          profile.settings?.favoriteColor ===
                            initialStates[profile.id]?.color)
                          ? "invisible"
                          : ""
                      }`}
                      onClick={() => handleUndoChanges(profile.id)}
                      title="Undo changes"
                    >
                      <FaUndo />
                    </button>

                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDeleteProfile(profile.id)}
                      disabled={profiles.length <= 1}
                      title={
                        profiles.length <= 1
                          ? "Cannot delete the last profile"
                          : "Delete profile"
                      }
                    >
                      <FaTrash />
                      {profiles.length <= 1 && (
                        <span className="delete-text">
                          Cannot delete last profile
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default UserProfileManager;
